import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle, CheckCircle2, Circle, Clock3, Database, Download, Eye, EyeOff, KeyRound, LockKeyhole, Mail, Monitor,
  MonitorSmartphone, MoreVertical, ShieldCheck, Smartphone, Trash2,
} from 'lucide-react';
import { accountAPI, authAPI, tenantAPI } from '../../../../api/client';
import { useAuth } from '../../../../context/AuthContext';
import { useTenant } from '../../../../context/TenantContext';
import { apiError, appUrl, goTo } from '../../../../lib/host';
import { useT } from '../../../../lib/i18n';
import { usePreferences } from '../../preferences/PreferencesContext';
import { ErrorBanner, formatDate, formatTime, useBasic } from '../../ui';
import PasswordModal from '../components/PasswordModal';
import { Card, SectionHead, ToggleRow } from '../components/SettingsUI';
import TwoFactorCard from '../components/TwoFactorCard';
import { saveZip } from './ProfileSection';

const RULES = [
  ['At least 8 characters', (p) => p.length >= 8],
  ['Include uppercase letter (A-Z)', (p) => /[A-Z]/.test(p)],
  ['Include lowercase letter (a-z)', (p) => /[a-z]/.test(p)],
  ['Include a number (0-9)', (p) => /\d/.test(p)],
  ['Include a special character (!@#$%)', (p) => /[^A-Za-z0-9]/.test(p)],
];
const TIMEOUTS = [[15, '15 minutes'], [30, '30 minutes'], [60, '1 hour'], [120, '2 hours'], [240, '4 hours'], [480, '8 hours']];

function PasswordInput({ id, value, onChange, placeholder, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <span className="st-control with-icon st-password">
      <KeyRound size={15} />
      <input id={id} type={show ? 'text' : 'password'} value={value} placeholder={placeholder} autoComplete={autoComplete} onChange={(e) => onChange(e.target.value)} />
      <button type="button" aria-label={show ? 'Hide password' : 'Show password'} onClick={() => setShow(!show)}>{show ? <Eye size={16} /> : <EyeOff size={16} />}</button>
    </span>
  );
}

function PasswordCard({ focus }) {
  const { t } = useT();
  const { notify } = useBasic();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ref = useRef(null);
  useEffect(() => { if (focus) ref.current?.querySelector('input')?.focus(); }, [focus]);
  const ok = RULES.every(([, test]) => test(form.next)) && form.next === form.confirm && form.current;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await authAPI.changePassword(form.current, form.next);
      setForm({ current: '', next: '', confirm: '' });
      const n = res.data.other_sessions_ended;
      notify(n ? t('Password updated. {n} other device(s) were signed out.', { n }) : t('Password updated.'));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="st-password-card">
      <form className="st-password-grid" onSubmit={submit} ref={ref}>
        <div>
          <div className="st-card-head"><span className="st-icon blue"><ShieldCheck size={18} /></span>
            <div className="st-card-title"><h3>{t('Password')}</h3><p>{t('Update your password regularly to keep your account secure.')}</p></div></div>
          <ErrorBanner message={error} />
          <label className="st-field" htmlFor="pw-current"><span className="st-label">{t('Current Password')}</span>
            <PasswordInput id="pw-current" value={form.current} placeholder={t('Enter current password')} autoComplete="current-password" onChange={(v) => setForm({ ...form, current: v })} /></label>
          <label className="st-field" htmlFor="pw-new"><span className="st-label">{t('New Password')}</span>
            <PasswordInput id="pw-new" value={form.next} placeholder={t('Enter new password')} autoComplete="new-password" onChange={(v) => setForm({ ...form, next: v })} /></label>
          <label className="st-field" htmlFor="pw-confirm"><span className="st-label">{t('Confirm New Password')}</span>
            <PasswordInput id="pw-confirm" value={form.confirm} placeholder={t('Confirm new password')} autoComplete="new-password" onChange={(v) => setForm({ ...form, confirm: v })} /></label>
          {form.confirm && form.next !== form.confirm && <small className="st-bad">{t("The new passwords don't match.")}</small>}
        </div>
        <div className="st-rules">
          <b>{t('Password Requirements')}</b>
          <ul>{RULES.map(([label, test]) => {
            const met = test(form.next);
            return <li key={label} className={met ? 'met' : ''}>{met ? <CheckCircle2 size={15} /> : <Circle size={15} />}{t(label)}</li>;
          })}</ul>
          <button className="primary-action" disabled={busy || !ok}>{busy ? t('Updating…') : t('Update Password')}</button>
        </div>
      </form>
    </Card>
  );
}

const deviceIcon = (d) => (/iphone|android|mobile/i.test(d) ? Smartphone : /mac|windows|linux/i.test(d) ? Monitor : MonitorSmartphone);
function ago(iso, t) {
  const m = Math.round((Date.now() - new Date(`${iso}Z`).getTime()) / 60000);
  if (m < 2) return t('Active now');
  if (m < 60) return t('{n} min ago', { n: m });
  if (m < 1440) return t('{n} hours ago', { n: Math.round(m / 60) });
  return t('{n} days ago', { n: Math.round(m / 1440) });
}

function SessionsCard({ focus }) {
  const { t } = useT();
  const { notify } = useBasic();
  const [rows, setRows] = useState(null);
  const [menu, setMenu] = useState(null);
  const [error, setError] = useState('');
  const ref = useRef(null);
  const load = useCallback(() => authAPI.sessions().then((r) => setRows(r.data)).catch((err) => setError(apiError(err))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (focus) ref.current?.scrollIntoView({ block: 'center' }); }, [focus, rows]);
  const others = (rows || []).filter((s) => !s.current).length;
  const signOutOthers = async () => {
    try {
      const res = await authAPI.signOutOthers();
      notify(t('{n} other device(s) signed out.', { n: res.data.sessions_ended }));
      load();
    } catch (err) { setError(apiError(err)); }
  };
  const end = async (id) => {
    setMenu(null);
    try { await authAPI.endSession(id); notify(t('That device was signed out.')); load(); } catch (err) { setError(apiError(err)); }
  };
  return (
    <div ref={ref}>
      <Card title={t('Active Sessions')} subtitle={t('Manage your logged-in devices and sessions.')} icon={MonitorSmartphone} tone="blue"
        aside={<button type="button" className="st-danger-soft" disabled={!others} onClick={signOutOthers}>{t('Sign out all devices')}</button>}>
        <ErrorBanner message={error} />
        <div className="st-sessions">
          {rows === null && <p className="st-muted">{t('Loading…')}</p>}
          {rows?.map((s) => {
            const Icon = deviceIcon(s.device);
            return (
              <div key={s.id} className="st-session">
                <Icon size={18} /><i className={s.current ? 'live' : ''} />
                <span><b>{s.device}</b><small>{s.ip || t('Unknown address')}{s.remember_me ? ` · ${t('Remembered')}` : ''}</small></span>
                {s.current
                  ? <span className="st-current"><em>{t('Current Session')}</em><small>{formatDate(s.created_at)}, {formatTime(s.created_at)}</small></span>
                  : <span className="st-current"><small>{ago(s.last_seen_at, t)}</small></span>}
                {!s.current && (
                  <span className="st-session-menu">
                    <button type="button" aria-label={t('More')} onClick={() => setMenu(menu === s.id ? null : s.id)}><MoreVertical size={15} /></button>
                    {menu === s.id && <div className="st-popover"><button type="button" onClick={() => end(s.id)}>{t('Sign out this device')}</button></div>}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

const AUDIT = {
  'tenant.renamed': 'Renamed the company', 'tenant.profile_updated': 'Updated company information', 'billing.plan_changed': 'Changed the plan',
  'member.invited': 'Invited a teammate', 'member.invite_revoked': 'Revoked an invitation', 'member.joined': 'Joined the company',
  'member.updated': 'Updated a member', 'member.removed': 'Removed a member', 'member.left': 'Left the company', 'flag.updated': 'Changed a feature',
  'contact.created': 'Added a contact', 'contact.updated': 'Edited a contact', 'contact.deleted': 'Deleted a contact',
  'account.data_exported': 'Downloaded their data',
};

function ActivityCard() {
  const { t } = useT();
  const [logs, setLogs] = useState(null);
  const [names, setNames] = useState({});
  useEffect(() => {
    tenantAPI.auditLogs().then((r) => setLogs(r.data.slice(0, 25))).catch(() => setLogs([]));
    tenantAPI.members().then((r) => setNames(Object.fromEntries(r.data.map((m) => [String(m.user_id), m.name])))).catch(() => {});
  }, []);
  return (
    <Card title={t('Company Activity')} subtitle={t('Latest changes in your company (owners and admins see this).')} icon={Clock3} tone="slate">
      <div className="table-scroll">
        <table className="full-page-table">
          <thead><tr><th>{t('When')}</th><th>{t('Who')}</th><th>{t('What')}</th></tr></thead>
          <tbody>
            {logs?.map((l) => (
              <tr key={l.id}><td>{formatDate(l.created_at)}, {formatTime(l.created_at)}</td>
                <td>{names[String(l.actor_user_id)] || (l.actor_user_id ? t('Former member') : t('System'))}</td>
                <td className="cell-wrap">{t(AUDIT[l.action] || l.action)}{l.details?.name ? `: ${l.details.name}` : ''}</td></tr>
            ))}
            {logs?.length === 0 && <tr><td colSpan={3} className="empty-state">{t('No activity yet.')}</td></tr>}
            {logs === null && <tr><td colSpan={3} className="empty-state">{t('Loading…')}</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Settings › Security: password, two-factor sign-in, devices, account alerts, your data and deleting the account. */
export default function SecuritySection() {
  const { t } = useT();
  const [params] = useSearchParams();
  const focus = params.get('focus');
  const { notify } = useBasic();
  const { logout } = useAuth();
  const { can } = useTenant();
  const { prefs, save } = usePreferences();
  const security = prefs.security || {};
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const twoFa = useRef(null);
  useEffect(() => { if (focus === '2fa') twoFa.current?.scrollIntoView({ block: 'center' }); }, [focus]);

  const set = (values) => {
    setError('');
    save('security', values).then(() => notify(t('Security settings saved.'))).catch((err) => setError(apiError(err)));
  };
  const exportData = async () => {
    try { saveZip(await accountAPI.exportData()); notify(t('Your data is downloading.')); } catch (err) { setError(apiError(err)); }
  };
  const deleteAccount = async (password) => {
    await accountAPI.deleteAccount(password, confirmText);
    await logout().catch(() => {});
    goTo(appUrl('/login'));
  };

  return (
    <>
      <SectionHead title={t('Security')} subtitle={t('Keep your account and data safe with security settings.')} />
      <ErrorBanner message={error} />
      <PasswordCard focus={focus === 'password'} />
      <div className="st-two">
        <div ref={twoFa}><TwoFactorCard /></div>
        <SessionsCard focus={focus === 'sessions'} />
      </div>
      <div className="st-two">
        <Card title={t('Account Security')} subtitle={t('Additional security settings for your account.')} icon={LockKeyhole} tone="purple">
          <ToggleRow icon={Mail} tone="slate" title={t('Login Alerts')} sub={t('Get notified when someone logs into your account.')} checked={security.login_alerts} onChange={(v) => set({ login_alerts: v })} />
          <ToggleRow icon={AlertCircle} tone="red" title={t('Suspicious Activity Alerts')} sub={t('Get alerts for unusual account activity.')} checked={security.suspicious_alerts} onChange={(v) => set({ suspicious_alerts: v })} />
          <ToggleRow icon={Clock3} tone="slate" title={t('Session Timeout')} sub={t('Automatically sign out after a period of inactivity.')}>
            <select className="st-compact-select" value={security.session_timeout || 120} aria-label={t('Session Timeout')} onChange={(e) => set({ session_timeout: Number(e.target.value) })}>
              {TIMEOUTS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
            </select>
          </ToggleRow>
          <ToggleRow icon={Monitor} tone="slate" title={t('Allow Remember Me')} sub={t('Let this device stay signed in.')} checked={security.allow_remember_me} onChange={(v) => set({ allow_remember_me: v })} />
        </Card>
        <Card title={t('Data & Privacy')} subtitle={t('Manage your data and privacy preferences.')} icon={Database} tone="blue">
          <div className="st-privacy-row">
            <span className="st-icon small orange"><Download size={16} /></span>
            <span><b>{t('Download My Data')}</b><small>{t('Export your account data (calls, contacts, settings, etc.)')}</small></span>
            <button type="button" className="secondary-action" onClick={exportData}>{t('Request Export')}</button>
          </div>
          <div className="st-privacy-row">
            <span className="st-icon small red"><Trash2 size={16} /></span>
            <span><b>{t('Delete Account')}</b><small>{t('Permanently delete your account and personal data.')}</small></span>
            <button type="button" className="st-danger-soft" onClick={() => { setConfirmText(''); setDeleting(true); }}>{t('Delete Account')}</button>
          </div>
        </Card>
      </div>
      {can('audit.read') && <ActivityCard />}

      {deleting && (
        <PasswordModal title={t('Delete your account?')} danger confirmLabel={t('Delete Account')} onClose={() => setDeleting(false)}
          message={t('Your login, profile, photo and preferences are deleted and you are signed out everywhere. Company records (calls, contacts) stay with your company. This cannot be undone.')}
          extra={<label className="field">{t('Type DELETE to confirm')}<input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoComplete="off" /></label>}
          onConfirm={deleteAccount} />
      )}
    </>
  );
}
